/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ListGroup } from "reactstrap";
import { listDevices } from "../../actions/Devices/actions";
import { listTenants } from "../../actions/Tenants/actions";
import useConfig from "../../actions/Tenants/config/configHook";
import { RootState } from "../../reducers";
import { Loading } from "../Helper/Loading";
import { Device, DeviceItem } from "./DeviceItem";

export const DataTestId = {
    LIST: "LIST",
    LIST_FTA: "LIST_FTA",
    LIST_OTHER: "LIST_OTHER",
} as const;

export function DeviceList(): React.ReactElement {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.auth.user);
    const devices = useSelector((state: RootState) => state.devices.devices) || [];
    const minis = devices.filter((i: Device) => i.type === "mini");
    const ftas = devices.filter((i: Device) => i.type === "fta");
    const otherDevices = devices.filter((i: Device) => i.type !== "mini" && i.type !== "fta");
    const selected_mini = useSelector((state: RootState) => state.devices.selected_mini);
    const selected_fta = useSelector((state: RootState) => state.devices.selected_fta);
    const selected_other = useSelector((state: RootState) => state.devices.selected_other);
    const isLoading = useSelector((state: RootState) => state.devices.isLoading);
    const config = useConfig();

    const dispatchListDevices = (): void => {
        dispatch(listDevices({}) as any);
    };

    // do init load of minis and fetch hearbeat and repeat
    useEffect(() => {
        dispatchListDevices();
        const process = setInterval(dispatchListDevices, 45000);
        return () => clearInterval(process);
    }, []);

    // Fetch list of tenants on component mount
    useEffect(() => {
        if (config.is_admin) {
            dispatch(listTenants() as any);
        }
    }, [config.is_admin, dispatch]);

    if (isLoading || !devices) {
        return (
            <div css={css`min-height: 100vh;`}>
                <Loading />
            </div>
        );
    }

    const renderDeviceSection = (
        devices: Device[],
        title: string,
        selectedDevice: Device | undefined,
        testId: string
    ): React.ReactElement | null => {
        if (devices.length === 0) return null;

        return (
            <>
                <h3 className="pt-3 pb-2">{title}</h3>
                <div className="pb-3">
                    <ListGroup data-testid={testId}>
                        {devices.map((device) => (
                            <DeviceItem
                                key={`${device.type}-${device.UID}`}
                                device={device}
                                selectedDevice={selectedDevice}
                                user={user}
                                config={config}
                            />
                        ))}
                    </ListGroup>
                </div>
            </>
        );
    };

    return (
        <>
            {renderDeviceSection(minis, "Mini", selected_mini, DataTestId.LIST)}
            {renderDeviceSection(ftas, "FTA", selected_fta, DataTestId.LIST_FTA)}
            {renderDeviceSection(otherDevices, "Other Devices", selected_other, DataTestId.LIST_OTHER)}
        </>
    );
}
